import { createContext, useCallback, useContext } from "react";
import CONFIG from "./CONFIG";
import useApiService from "./ApiService";

const PostCallAnalysisApiContext = createContext(null);

export function PostCallAnalysisApiProvider({ children }) {
    const { get, post, put, del } = useApiService(CONFIG.SERVER_CHANNEL);

    // =========================================================================
    // Configuration CRUD
    // =========================================================================

    const getConfigs = useCallback(async (skip = 0, limit = 100) => {
        try {
            const response = await get(`/v1/post-call-analysis?skip=${skip}&limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching post-call analysis configs:", error);
            throw error;
        }
    }, [get]);

    const getConfigById = useCallback(async (configId, includeDetails = false) => {
        try {
            const response = await get(`/v1/post-call-analysis/${configId}?include_details=${includeDetails}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching post-call analysis config by ID:", error);
            throw error;
        }
    }, [get]);

    const createConfig = useCallback(async (configData) => {
        try {
            const response = await post(`/v1/post-call-analysis`, configData);
            return response.data;
        } catch (error) {
            console.error("Error creating post-call analysis config:", error);
            throw error;
        }
    }, [post]);

    const updateConfig = useCallback(async (configId, configData) => {
        try {
            const response = await put(`/v1/post-call-analysis/${configId}`, configData);
            return response.data;
        } catch (error) {
            console.error("Error updating post-call analysis config:", error);
            throw error;
        }
    }, [put]);

    const deleteConfig = useCallback(async (configId) => {
        try {
            const response = await del(`/v1/post-call-analysis/${configId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting post-call analysis config:", error);
            throw error;
        }
    }, [del]);

    // =========================================================================
    // Channel Management
    // =========================================================================

    const setChannels = useCallback(async (configId, channelIds) => {
        try {
            const response = await put(`/v1/post-call-analysis/${configId}/channels`, { channel_ids: channelIds });
            return response.data;
        } catch (error) {
            console.error("Error setting channels:", error);
            throw error;
        }
    }, [put]);

    const addChannel = useCallback(async (configId, channelId) => {
        try {
            const response = await post(`/v1/post-call-analysis/${configId}/channels/${channelId}`);
            return response.data;
        } catch (error) {
            console.error("Error adding channel:", error);
            throw error;
        }
    }, [post]);

    const removeChannel = useCallback(async (configId, channelId) => {
        try {
            const response = await del(`/v1/post-call-analysis/${configId}/channels/${channelId}`);
            return response.data;
        } catch (error) {
            console.error("Error removing channel:", error);
            throw error;
        }
    }, [del]);

    // =========================================================================
    // Metrics Management
    // =========================================================================

    const getMetrics = useCallback(async (configId) => {
        try {
            const response = await get(`/v1/post-call-analysis/${configId}/metrics`);
            return response.data;
        } catch (error) {
            console.error("Error fetching metrics:", error);
            throw error;
        }
    }, [get]);

    const getMetricById = useCallback(async (configId, metricId) => {
        try {
            const response = await get(`/v1/post-call-analysis/${configId}/metrics/${metricId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching metric by ID:", error);
            throw error;
        }
    }, [get]);

    const createMetric = useCallback(async (configId, metricData) => {
        try {
            const response = await post(`/v1/post-call-analysis/${configId}/metrics`, metricData);
            return response.data;
        } catch (error) {
            console.error("Error creating metric:", error);
            throw error;
        }
    }, [post]);

    const updateMetric = useCallback(async (configId, metricId, metricData) => {
        try {
            const response = await put(`/v1/post-call-analysis/${configId}/metrics/${metricId}`, metricData);
            return response.data;
        } catch (error) {
            console.error("Error updating metric:", error);
            throw error;
        }
    }, [put]);

    const deleteMetric = useCallback(async (configId, metricId) => {
        try {
            const response = await del(`/v1/post-call-analysis/${configId}/metrics/${metricId}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting metric:", error);
            throw error;
        }
    }, [del]);

    return (
        <PostCallAnalysisApiContext.Provider value={{
            // Configs
            getConfigs,
            getConfigById,
            createConfig,
            updateConfig,
            deleteConfig,
            // Channels
            setChannels,
            addChannel,
            removeChannel,
            // Metrics
            getMetrics,
            getMetricById,
            createMetric,
            updateMetric,
            deleteMetric
        }}>
            {children}
        </PostCallAnalysisApiContext.Provider>
    );
}

export function usePostCallAnalysisApi() {
    const context = useContext(PostCallAnalysisApiContext);
    if (!context) {
        throw new Error("usePostCallAnalysisApi must be used within a PostCallAnalysisApiProvider");
    }
    return context;
}
